import React, { useState } from 'react';
import { MediaFile } from '../types';
import ImageViewer from './ImageViewer';
import Alert from './ErrorDisplay';

interface GalleryPageProps {
    media: MediaFile[];
}

const GalleryPage: React.FC<GalleryPageProps> = ({ media }) => {
    const [selectedImage, setSelectedImage] = useState<MediaFile | null>(null);
    const [videoModalSrc, setVideoModalSrc] = useState<string | null>(null);

    if (!media || media.length === 0) {
        return (
            <Alert
                title="Keine Medien"
                message="Es wurden noch keine Fotos oder Videos hochgeladen."
                type="info"
            />
        );
    }

    const images = media.filter(m => m.mimetype.startsWith('image/'));
    const videos = media.filter(m => m.mimetype.startsWith('video/'));

    return (
        <div className="space-y-6">
            {/* Images Section */}
            {images.length > 0 && (
                <section>
                    <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-pink-600" style={{ fontSize: '1.25rem' }}>
                            photo_library
                        </span>
                        Fotos
                        <span className="text-sm font-normal text-gray-400">({images.length})</span>
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                        {images.map(image => {
                            const aspectRatio = image.width && image.height ? image.height / image.width : 1;
                            return (
                                <button
                                    key={image.id}
                                    onClick={() => setSelectedImage(image)}
                                    className="relative w-full overflow-hidden rounded-lg bg-gray-100 hover:opacity-90 active:scale-95 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-pink-500 shadow-sm"
                                    aria-label={image.caption || image.filename}
                                    style={{ aspectRatio: `1 / ${aspectRatio}` }}
                                >
                                    <img
                                        src={image.thumbnailUrl || image.url}
                                        alt={image.caption || image.filename}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                    {image.caption && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent px-2 py-1">
                                            <p className="text-white text-xs truncate">{image.caption}</p>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Videos Section */}
            {videos.length > 0 && (
                <section>
                    <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-pink-600" style={{ fontSize: '1.25rem' }}>
                            video_library
                        </span>
                        Videos
                        <span className="text-sm font-normal text-gray-400">({videos.length})</span>
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                        {videos.map(video => {
                            const aspectRatio = video.width && video.height ? video.height / video.width : 9 / 16;
                            return (
                                <button
                                    key={video.id}
                                    onClick={() => setVideoModalSrc(video.url)}
                                    className="relative w-full overflow-hidden rounded-lg bg-gray-900 hover:opacity-90 active:scale-95 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-pink-500 shadow-sm"
                                    aria-label={video.caption || video.filename}
                                    style={{ aspectRatio: `1 / ${aspectRatio}` }}
                                >
                                    {video.thumbnailUrl ? (
                                        <img
                                            src={video.thumbnailUrl}
                                            alt={video.caption || video.filename}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                            <span className="material-symbols-outlined text-white/40" style={{ fontSize: '3rem' }}>
                                                movie
                                            </span>
                                        </div>
                                    )}
                                    {/* Play overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20">
                                            <span className="material-symbols-outlined text-white" style={{ fontSize: '2rem' }}>
                                                play_arrow
                                            </span>
                                        </div>
                                    </div>
                                    {video.caption && (
                                        <div className="px-3 py-2 bg-gray-900 text-left">
                                            <p className="text-white/80 text-sm truncate">{video.caption}</p>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Image Viewer */}
            {selectedImage && (
                <ImageViewer
                    src={selectedImage.url}
                    alt={selectedImage.caption || selectedImage.filename}
                    onClose={() => setSelectedImage(null)}
                />
            )}

            {/* Video Modal */}
            {videoModalSrc && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
                    onClick={() => setVideoModalSrc(null)}
                >
                    <button
                        onClick={() => setVideoModalSrc(null)}
                        className="absolute z-[10000] p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 backdrop-blur-md border border-white/10"
                        style={{
                            top: 'calc(1rem + env(safe-area-inset-top, 0px))',
                            right: 'calc(1rem + env(safe-area-inset-right, 0px))'
                        }}
                        aria-label="Schließen"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                    <video
                        src={videoModalSrc}
                        controls
                        autoPlay
                        className="max-w-full max-h-full rounded-lg shadow-2xl"
                        style={{ maxHeight: '90dvh' }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

export default GalleryPage;


